import * as React from "react";
import { Typography, Link, Box, Tab, Stack, OutlinedInput, InputAdornment, Avatar, Button } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInFullOutlinedIcon from "@mui/icons-material/OpenInFullOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Link as RouterLink } from "react-router";
import { routePaths } from "../../../Router/router";
import Panel from "../Panel/panel";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import PanelTabs from "../PanelTabs";
import PanelAccordion from "../PanelAccordion/panel-accordion";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import ProviderRequestCard from "../../Cards/ProviderRequestCard";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import { ProviderAndStorage } from "../../../Core/Api/Types/provider-and-storage";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { RejectionRating } from "../../../Core/Api/Types/rejection-rating";
import TableCellBool from "../../Table/TableCellBool";
import { ingestService, userService } from "../../../Core/Api/services";
import { DbAssetObjectInfos } from "../../../Core/Api/Types/db-asset-object-infos";
import moment from "moment";
import TableCellName from "../../Table/TableCellName";
import { DbUserBasicInfos } from "../../../Core/Api/Types/db-user-basic-infos";
import TableCellProgressBar from "../../Table/TableCellProgressBar";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { Settings } from "handsontable/plugins/contextMenu";
import { SettingPath } from "../../../Core/Api/Types/setting-path";
import { useTranslation } from "react-i18next";

export interface ProvidersDetailPanelProps {
  width?: number;
  open: boolean;
  provider: ProviderAndStorage & { rank: RejectionRating } & { index: number };
  onClose?: React.MouseEventHandler;
}

const ProvidersDetailPanel: React.FC<ProvidersDetailPanelProps> = (props) => {
  const { t } = useTranslation();
  const { provider } = props;
  const [value, setValue] = React.useState(0);
  const [assets, setAssets] = React.useState<DbAssetObjectInfos[]>([]);
  const [users, setUsers] = React.useState<DbUserBasicInfos[]>([]);
  const [requestSearch, setRequestSearch] = React.useState<string>("");
  const [userSearch, setUserSearch] = React.useState<string>("");

  // useEffect
  React.useEffect(() => {
    if (provider) {
      const rejections = ingestService.GetRejectionsByProviderId(provider.provider!.id);
      const users = userService.GetUsersListByProviderId(provider.provider!.id);

      Promise.all([rejections, users]).then(([rejectionsResponse, usersResponse]) => {
        if (rejectionsResponse.assets !== null) {
          setAssets(rejectionsResponse.assets);
        }
        if (usersResponse.users !== null) {
          setUsers(usersResponse.users);
        }
      });
    }
  }, [provider]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  if (provider)
    return (
      <Panel
        header={
          <PanelHeader
            title={t("panel:title.providerDetails")}
            onClose={props.onClose as PanelHeaderProps["onClose"]}
            rightContent={
              <Link
                component={RouterLink}
                to={`${routePaths.providers.path}/${provider.provider?.id}`}
                underline="hover"
                color="primary.text"
              >
                <OpenInFullOutlinedIcon sx={{ mr: 0.5 }} /> {t("common:open")}
              </Link>
            }
          />
        }
        reload={provider.provider?.id}
        open={props.open}
        onClose={props.onClose}
      >
        <Stack direction="row" justifyContent="space-between" pb={2} spacing={3}>
          <Stack flex={1}>
            <Typography variant="h6" color="text.placeholder">
              {t("common:provider")}
            </Typography>
            <Typography variant="h3" component="h2">
              {provider.provider?.labName}
            </Typography>
          </Stack>
          <Avatar src="https://picsum.photos/64/64" sx={{ width: 60, height: 60, flexShrink: 0 }} />
        </Stack>
        <PanelTabs value={value} onChange={handleChange} aria-label="basic tabs example" variant="fullWidth">
          <Tab label={t("common:details")} {...a11yProps(0)} />
          <Tab label={t("common:users")} {...a11yProps(1)} />
          <Tab label={t("common:rejectedRequests")} {...a11yProps(1)} />
        </PanelTabs>
        <Box mt={2}>
          <PanelTabContent id="providers-details-panel" activePanelIndex={value} index={0}>
            <PanelAccordion title={t("panel:accordionTitle.globalInfos")}>
              <Stack spacing={1.5}>
                <MetadataListTextItem title={t("menu:title.fullName")}>
                  {provider.provider?.labName || "-"}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("common:description")}>
                  {provider.provider?.labDescription || "-"}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.emailAddress")}>
                  {provider.provider?.labEmail || "-"}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.asperaHost")}>
                  {provider.provider?.asperaAccount?.asperaHost || "-"}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.asperaLogin")}>
                  {provider.provider?.asperaAccount?.asperaLogin || "-"}
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.asperaPassword")}>
                  {provider.provider?.asperaAccount?.asperaPassword || "-"}
                </MetadataListTextItem>
                {provider.storageSource?.storage !== undefined && (
                  <MetadataListTextItem title={t("menu:title.folderRootPath")}>
                    {((provider.storageSource?.storage?.path as SettingPath | null)?.linux as String) || "-"}
                  </MetadataListTextItem>
                )}
                <MetadataListTextItem title={t("common:enability")}>
                  <TableCellBool value={!provider.provider?.disabled} />
                </MetadataListTextItem>
                <MetadataListTextItem title={t("menu:title.affinityCheckEnability")}>
                  <TableCellBool value={!provider.provider?.disableAffinityCheck} />
                </MetadataListTextItem>
              </Stack>
            </PanelAccordion>
            <PanelAccordion title={t("panel:accordionTitle.ratingsInfos")}>
              <Stack spacing={1.5}>
                <MetadataListTextItem title={t("common:rank")}>
                  {provider.index
                    ? provider.index === 1
                      ? provider.index + "st"
                      : provider.index === 2
                      ? provider.index + "nd"
                      : provider.index === 3
                      ? provider.index + "rd"
                      : provider.index + "th"
                    : "-"}
                </MetadataListTextItem>
                {provider.index && provider?.rank.acceptedPercentage && (
                  <MetadataListTextItem title={t("menu:title.acceptedRate")}>
                    <TableCellProgressBar progress={provider?.rank.acceptedPercentage} />
                  </MetadataListTextItem>
                )}
                <MetadataListTextItem title={t("menu:title.requestCount")}>
                  {provider?.rank.requestsCount || "-"}
                </MetadataListTextItem>

                {provider.rank.requestsCount && provider.rank.rejectedPercentage && (
                  <MetadataListTextItem title={t("menu:title.rejectionsCount")}>
                    {Math.round((provider?.rank.requestsCount * provider?.rank.rejectedPercentage) / 100) || "-"}
                  </MetadataListTextItem>
                )}
              </Stack>
            </PanelAccordion>
          </PanelTabContent>
          <PanelTabContent id="providers-details-panel" activePanelIndex={value} index={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} mb={2}>
              <OutlinedInput
                label={t("common:search")}
                sx={{ width: "100%" }}
                placeholder={t("common:search")}
                onChange={(e) => setUserSearch(e.currentTarget.value)}
                endAdornment={
                  <InputAdornment position="end">
                    <SearchIcon />
                  </InputAdornment>
                }
              />
            </Stack>
            <Stack spacing={1.5} mb={2}>
              {users
                .filter(
                  (user) =>
                    user.firstName?.toLowerCase().includes(userSearch) ||
                    user.lastName?.toLowerCase().includes(userSearch)
                )
                .map((user) => (
                  <TableCellName
                    key={user.id}
                    data={{ value: { firstname: user.firstName || "-", lastname: user.lastName || "-", id: user.id } }}
                  />
                ))}
              {users === undefined || (users.length < 1 && <Typography>{t("component:body.noUsers")}</Typography>)}
            </Stack>
          </PanelTabContent>

          <PanelTabContent id="providers-details-panel" activePanelIndex={value} index={2}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5} mb={2}>
              <OutlinedInput
                label={t("common:standard")}
                placeholder={t("common:search")}
                endAdornment={<SearchIcon />}
                onChange={(e) => setRequestSearch(e.currentTarget.value)}
                sx={{ flex: 1 }}
              />
              <Button variant="icon">
                <FilterListIcon />
              </Button>
            </Stack>

            <Stack spacing={2}>
              {assets
                .filter((asset) => asset.parentTitleName?.toLowerCase().includes(requestSearch))
                .map((asset) => (
                  <ProviderRequestCard
                    key={asset.id}
                    title={asset.parentTitleName || "-"}
                    desc={asset.comment ? asset.comment + "/" + asset.name : asset.name}
                    startDate={
                      moment(asset.creationInfos?.date).format(GlobalConst.MOMENT_DATE_FORMAT_FULL).toString() || "-"
                    }
                    endDate={
                      asset.creationInfos?.endDate
                        ? moment(asset.creationInfos?.endDate).format(GlobalConst.MOMENT_DATE_FORMAT_FULL).toString()
                        : "-"
                    }
                    status={asset.status}
                    comments={asset.updateInfos?.length || 0}
                    assetType={asset.type as ObjectType}
                  />
                ))}
            </Stack>
            {assets === undefined ||
              (assets.length < 1 && <Typography>{t("panel:providersDetailPanel.body.noRejectedRequests")}</Typography>)}
          </PanelTabContent>
        </Box>
      </Panel>
    );
  else {
    return <span></span>;
  }
};

function a11yProps(index: number) {
  return {
    id: `providers-details-panel-tab-${index}`,
    "aria-controls": `providers-details-panel-tabpanel-${index}`,
  };
}

export default ProvidersDetailPanel;
